import {
  ExclamationTriangleIcon,
  PencilIcon,
  XMarkIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import { City, State } from "../../../Models";
import { Dialog, Transition } from "@headlessui/react";
import { Fragment, useCallback, useEffect, useRef, useState } from "react";
import Loading from "../../Shared/Loading";
import { useNotification } from "../../../context/NotificationContext";
import { isQuarantineCity } from "../../../Helpers";
import { QuarantineDataType } from "../Quarantine";
import { getAlternateNameSuggestions } from "../../../api/quarantine.api";

export default function SubCityConfirmationPopup({
  isOpen,
  onCancel,
  onConfirm,
  selectedRows,
  majorCityOptions,
  states,
  updateSelectedRows,
  gptCityDetails,
  token
}: {
  onCancel: Function;
  isOpen: boolean;
  onConfirm: Function;
  selectedRows: QuarantineDataType[];
  majorCityOptions: City[];
  states: State[];
  updateSelectedRows: Function;
  gptCityDetails: any;
  token:string;
}) {
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchValue, setSearchValue] = useState<string>("");
  const [selectedOption, setSelectedOption] = useState<City | null>(null);
  const [majorCityId, setMajorCityId] = useState<string>("");
  const [editingIndex, setEditingIndex] = useState<number>(-1);
  const { success, error: notifyError } = useNotification();
  const [editedName, setEditedName] = useState<string>("");
  const [editedState, setEditedState] = useState<State | null>(null);
  const [replaceCommon, setReplaceCommon] = useState<boolean[]>([]);
  const [isFocused, setIsFocused] = useState(false);
  const cancelButtonRef = useRef(null);

  // Per-row alternate names (independent of edit mode)
  const [alternateNames, setAlternateNames] = useState<string[]>([]);

  // Per-row loading state for AI suggestions
  const [suggestionLoading, setSuggestionLoading] = useState<boolean[]>([]);
  const [gptApplied, setGptApplied] = useState<boolean>(false);
  const [originalStateIds, setOriginalStateIds] = useState<string[]>([]);

  // Initialize states based on selectedRows length (once per open)
  useEffect(() => {
    if (!open) return;
    setReplaceCommon(new Array(selectedRows.length).fill(true));
    setAlternateNames(selectedRows.map((row) => row.alternateNames || ""));
    setSuggestionLoading(new Array(selectedRows.length).fill(false));
  }, [open, selectedRows.length]);

  useEffect(() => {
    setOpen(isOpen);
    if (!isOpen) {
      setGptApplied(false);
      setOriginalStateIds([]);
    }
    if (isOpen) {
      setOriginalStateIds(selectedRows.map((row) => row.state.id));
    }
  }, [isOpen]);

  useEffect(() => {
    if (!open || gptApplied) return;

    selectedRows.forEach((row, index) => {
      const cityDetails = gptCityDetails[row.id];
      if (!cityDetails || !open) return;

      const matchedCity = majorCityOptions.find(
        (city) =>
          city.name.toLowerCase() === cityDetails.major_city?.toLowerCase() &&
          city.id !== row.id
      );

      if (matchedCity && index === 0) {
        setSelectedOption(matchedCity);
        setSearchValue(matchedCity.name);
        setMajorCityId(matchedCity.id);
      }

      const correctedName = cityDetails?.corrected_name || "";

      // Compute the desired final values for this row
      let nextName = row.name;
      const nextAlt = alternateNames[index] || "";
      const currentAlt = row.alternateNames || "";

      if (correctedName) {
        nextName = correctedName;
      } else {
        // Nothing to change from GPT for this row
        return;
      }

      // Only push an update if something actually changes
      if (row.name !== nextName || currentAlt !== nextAlt) {
        updateSelectedRows(index, nextName, row.state.id, nextAlt);
      }
    });

    setGptApplied(true);
  }, [open, gptApplied, gptCityDetails, majorCityOptions, selectedRows, alternateNames, updateSelectedRows]);

  const hasAnyStateCorrection = selectedRows.some((row, index) => {
    const details = gptCityDetails[row.id];
    const correctedStateId = details?.correctedStateId as string | undefined;
    const originalId = originalStateIds[index];
    return !!correctedStateId && !!originalId && correctedStateId !== originalId;
  });

  // =========================================================================
  // HANDLERS
  // =========================================================================

  const handleClosePopup = () => {
    onCancel();
    setOpen(false);
  };

  const handleMakeSubCity = () => {
    // Build updated rows from what is currently shown in the popup (including unsaved edits)
    const updatedRows = selectedRows.map((row, index) => {
      const baseState = states.find((s) => s.id === row.state.id) || row.state;

      // If this row is being edited, prefer the in-modal edited values
      if (index === editingIndex && editedState) {
        return {
          ...row,
          name: editedName,
          state: editedState,
          alternateNames: alternateNames[index] || "",
        };
      }

      return {
        ...row,
        name: row.name,
        state: baseState,
        alternateNames: alternateNames[index] || "",
      };
    });

    // Push the final values back to parent so table state matches the popup
    updatedRows.forEach((row, index) => {
      updateSelectedRows(
        index,
        row.name,
        row.state.id,
        row.alternateNames || ""
      );
    });

    // Pass updated rows directly to handler
    onConfirm(majorCityId, replaceCommon, updatedRows);
    setOpen(false);
  };

  const handleOptionSelect = (option: City) => {
    setSelectedOption(option);
    setSearchValue(option.name);
    setMajorCityId(option.id);
  };

  const handleAlternateNameChange = (index: number, value: string) => {
    setAlternateNames((prev) => {
      const next = [...prev];
      next[index] = value;
      return next;
    });
  };

  // AI Suggestion handler
  const handleGetSuggestions = async (index: number) => {
    const row = selectedRows[index];
    const cityName = getDisplayName(index);
    const stateName = getDisplayState(index);

    if (!cityName) {
      notifyError("Error", "City name is required");
      return;
    }

    setSuggestionLoading((prev) => {
      const next = [...prev];
      next[index] = true;
      return next;
    });

    try {
      const response = await getAlternateNameSuggestions(cityName, stateName, token);

      if (response && response.alternateNames) {
        const suggestions = response.alternateNames;
        const existingNames = alternateNames[index]
          ? alternateNames[index].split(",").map((n) => n.trim().toLowerCase())
          : [];

        const newNames = suggestions.filter(
          (name: string) => !existingNames.includes(name.toLowerCase())
        );

        const merged = alternateNames[index]
          ? `${alternateNames[index]}, ${newNames.join(", ")}`
          : newNames.join(", ");

        setAlternateNames((prev) => {
          const next = [...prev];
          next[index] = merged.replace(/^,\s*/, "").replace(/,\s*$/, "");
          return next;
        });

        success(
          "Suggestions Added",
          `${newNames.length} alternate name(s) suggested`
        );
      } else {
        notifyError("No Suggestions", "No alternate names found for this city");
      }
    } catch (error: any) {
      notifyError(
        "Error",
        error.message || "Failed to get alternate name suggestions"
      );
    } finally {
      setSuggestionLoading((prev) => {
        const next = [...prev];
        next[index] = false;
        return next;
      });
    }
  };

  const startEditing = (index: number) => {
    setEditingIndex(index);
    const cityDetails = gptCityDetails[selectedRows[index].id];
    setEditedName(cityDetails?.corrected_name || selectedRows[index].name);
    setEditedState(selectedRows[index].state);
  };

  const cancelEditing = () => {
    setEditingIndex(-1);
    setEditedName("");
    setEditedState(null);
  };

  const handleSave = () => {
    if (editingIndex === -1 || !editedState) return;
    setLoading(true);
    try {
      updateSelectedRows(
        editingIndex,
        editedName,
        editedState.id,
        alternateNames[editingIndex] || ""
      );
      success("Success", "City updated successfully");
      setEditingIndex(-1);
    } catch (error) {
      notifyError("Error", "Failed to update city");
    } finally {
      setLoading(false);
    }
  };

  const filteredOptions = majorCityOptions.filter((option) => {
    const searchLower = searchValue.toLowerCase().trim();

    if (searchLower === "") {
      return true;
    }

    const nameMatches = option.name.toLowerCase().includes(searchLower);
    const alternateNamesMatches = option.alternateNames
      ? option.alternateNames
          .split(",")
          .some((altName) =>
            altName.trim().toLowerCase().includes(searchLower)
          )
      : false;

    return nameMatches || alternateNamesMatches;
  });

  const handleReplaceCheckbox = (
    event: React.ChangeEvent<HTMLInputElement>,
    index: number
  ) => {
    setReplaceCommon((prevState) => {
      const newState = [...prevState];
      newState[index] = event.target.checked;
      return newState;
    });
  };

  const getDisplayName = useCallback((index: number) => {
    const cityDetails = gptCityDetails[selectedRows[index].id];
    return cityDetails?.corrected_name || selectedRows[index].name;
  }, [gptCityDetails, selectedRows]);

  const getDisplayState = useCallback(
    (index: number) => selectedRows[index].state.name,
    [selectedRows]
  );

  return (
    <Transition.Root show={open} as={Fragment}>
      <Dialog
        as="div"
        className="relative z-50"
        initialFocus={cancelButtonRef}
        onClose={handleClosePopup}
      >
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-200"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity backdrop-blur-sm" />
        </Transition.Child>

        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-center justify-center p-4 text-center">
            <Transition.Child
              as={Fragment}
              enter="ease-out duration-300"
              enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              enterTo="opacity-100 translate-y-0 sm:scale-100"
              leave="ease-in duration-200"
              leaveFrom="opacity-100 translate-y-0 sm:scale-100"
              leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            >
              <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white dark:bg-darkbg dark:text-white shadow-xl transition-all w-full max-w-5xl mx-auto">
                <div className="p-6">
                  <div className="flex items-center mb-4">
                    <ExclamationTriangleIcon
                      className="h-6 w-6 text-red-600"
                      aria-hidden="true"
                    />
                    <div className="text-lg ml-2 font-semibold">
                      Are You Sure?
                    </div>
                  </div>

                  {hasAnyStateCorrection && (
                    <div className="mb-4 rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/30 dark:border-amber-700 dark:text-amber-200 flex items-start gap-2">
                      <ExclamationTriangleIcon className="h-5 w-5 mt-0.5" />
                      <span>
                        For one or more cities, GPT suggests a different state than the one currently stored.
                        Please review the suggested states below and update if needed.
                      </span>
                    </div>
                  )}

                  <div className="flex items-center justify-between mb-4">
                    <div className="font-semibold">Choose Major City:</div>
                    <div className="relative w-64">
                      <div className="relative flex items-center">
                        <input
                          type="text"
                          name="search"
                          id="search"
                          className="block w-full border-0 pl-3 py-2 text-gray-900 ring-1 ring-inset ring-gray-300 dark:ring-gray-500 placeholder:text-gray-400 focus:ring-1 focus:ring-inset focus:ring-entntorange sm:text-sm bg-transparent dark:text-gray-300 dark:placeholder:text-gray-400 rounded-md"
                          placeholder="Search"
                          value={searchValue}
                          onChange={(e) => setSearchValue(e.target.value)}
                          autoComplete="off"
                          onFocus={() => setIsFocused(true)}
                          onBlur={() =>
                            setTimeout(() => setIsFocused(false), 1000)
                          }
                        />
                        {selectedOption && (
                          <div
                            className="absolute right-2 text-gray-400 hover:text-gray-500 cursor-pointer dark:hover:text-gray-300"
                            onClick={() => {
                              setSelectedOption(null);
                              setSearchValue("");
                              setMajorCityId("");
                            }}
                          >
                            <XMarkIcon className="h-5 w-5" />
                          </div>
                        )}
                      </div>
                      {filteredOptions.length > 0 &&
                        isFocused &&
                        !selectedOption && (
                          <div className="absolute z-10 w-full mt-1 bg-white shadow-lg rounded-md ring-1 ring-black ring-opacity-5 dark:bg-darkbglight">
                            <ul className="max-h-40 overflow-auto py-1">
                              {filteredOptions.map((option, index) => (
                                <li
                                  key={index}
                                  className="cursor-pointer select-none relative py-2 pl-3 pr-9 text-gray-900 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                                  onClick={() => handleOptionSelect(option)}
                                >
                                  {option.name}{" "}
                                  {option?.alternateNames
                                    ? `(${option.alternateNames})`
                                    : ""}
                                </li>
                              ))}
                            </ul>
                          </div>
                        )}
                    </div>
                  </div>

                  <p className="mb-2 text-sm text-gray-500 dark:text-gray-400">
                    Total Selected Cities: {selectedRows.length}
                  </p>
                  <p className="mb-3 text-gray-500 text-xs">
                    Note: Sub-cities under the listed major cities will now
                    belong to the new Major City.
                  </p>

                  <div className="bg-slate-100 dark:bg-darkbglight rounded-md mb-4">
                    <ul className="list-none max-h-64 overflow-y-auto pr-4 dark:text-gray-400">
                      {selectedRows.map((column, index) => (
                        <li
                          key={column.id}
                          className="flex flex-col border-b border-gray-200 last:border-b-0 py-2"
                        >
                          {/* Row 1: City name, state, edit button, replace checkbox */}
                          <div className="flex items-center justify-between">
                            {editingIndex === index ? (
                              <div className="pl-4 flex items-center gap-2 flex-wrap">
                                <span className="font-semibold">{`${index + 1}.`}</span>
                                <input
                                  type="text"
                                  value={editedName}
                                  onChange={(e) => setEditedName(e.target.value)}
                                  className="border rounded px-2 py-1"
                                  placeholder="City name"
                                />
                                <select
                                  value={editedState?.id || ""}
                                  onChange={(e) => {
                                    const stateId = e.target.value;
                                    const state = states.find(
                                      (s) => s.id === stateId
                                    );
                                    if (state) setEditedState(state);
                                  }}
                                  className="border rounded px-2 py-1 w-[200px]"
                                >
                                  <option value="">Select State</option>
                                  {states.map((state) => (
                                    <option key={state.id} value={state.id}>
                                      {state.name}
                                    </option>
                                  ))}
                                </select>
                                <button
                                  onClick={handleSave}
                                  className="bg-green-500 text-white px-2 py-1 rounded hover:bg-green-600"
                                  disabled={loading || !editedState}
                                >
                                  Save
                                </button>
                                <button
                                  onClick={cancelEditing}
                                  className="bg-gray-300 text-gray-700 px-2 py-1 rounded hover:bg-gray-400"
                                  disabled={loading}
                                >
                                  Cancel
                                </button>
                              </div>
                            ) : (
                              <div className="pl-4 py-1 font-semibold flex flex-col items-start gap-1">
                                <span className="flex items-center gap-2">
                                  <span>{`${index + 1}. ${getDisplayName(index)} (${getDisplayState(index)})`}</span>
                                  <div className="flex items-center gap-1">
                                    <PencilIcon
                                      height={12}
                                      className="hover:cursor-pointer text-blue-500"
                                      onClick={() => startEditing(index)}
                                    />
                                    {(() => {
                                      const details =
                                        gptCityDetails[column.id] ||
                                        (column as any).actiongpt;
                                      const correctedStateId =
                                        details?.correctedStateId as string | undefined;
                                      const originalId = originalStateIds[index];

                                      if (
                                        !correctedStateId ||
                                        !originalId ||
                                        correctedStateId === originalId
                                      ) {
                                        return null;
                                      }

                                      return (
                                        <ExclamationTriangleIcon
                                          className="w-4 h-4 text-yellow-500"
                                          title="GPT suggests a different state for this city"
                                        />
                                      );
                                    })()}
                                  </div>
                                </span>
                                {(() => {
                                  const details =
                                    gptCityDetails[column.id] ||
                                    (column as any).actiongpt;
                                  const correctedStateId =
                                    details?.correctedStateId as string | undefined;
                                  const originalId = originalStateIds[index];
                                  const correctedState =
                                    correctedStateId &&
                                    states.find((s) => s.id === correctedStateId);

                                  if (
                                    !correctedStateId ||
                                    !correctedState ||
                                    !originalId ||
                                    correctedStateId === originalId
                                  ) {
                                    return null;
                                  }

                                  return (
                                    <span className="text-xs font-light text-grey-700">
                                      GPT suggests this city may belong to{" "}
                                      <span className="font-semibold">
                                        {correctedState.name}
                                      </span>{" "}
                                      instead of{" "}
                                      <span className="font-semibold">
                                        {column.state.name}
                                      </span>
                                      . Please review and update the state if needed.
                                    </span>
                                  );
                                })()}
                              </div>
                            )}

                            {editingIndex !== index &&
                              isQuarantineCity(column) &&
                              (column as any).replacedCities &&
                              (column as any).replacedCities?.length > 0 && (
                                <span className="pl-4 py-1 font-semibold flex items-center">
                                  <label htmlFor={`replaceCity-sub-${index}`}>
                                    Also update{" "}
                                    {(column as any).replacedCities?.length}{" "}
                                    common cities
                                  </label>
                                  <input
                                    type="checkbox"
                                    id={`replaceCity-sub-${index}`}
                                    className="ml-2 rounded z-10 text-indigo-600"
                                    checked={replaceCommon[index]}
                                    onChange={(e) =>
                                      handleReplaceCheckbox(e, index)
                                    }
                                  />
                                </span>
                              )}
                          </div>

                          {/* Row 2: Alternate names field with AI suggestion button */}
                          <div className="pl-8 pt-1 flex items-center gap-2">
                            <label className="text-xs text-gray-600 dark:text-gray-400 whitespace-nowrap">
                              Alternate names:
                            </label>
                            <input
                              type="text"
                              value={alternateNames[index] || ""}
                              onChange={(e) =>
                                handleAlternateNameChange(index, e.target.value)
                              }
                              className="border rounded px-2 py-1 text-sm w-72 placeholder:text-gray-400"
                              placeholder="Enter alternate names separated by comma"
                            />
                            <button
                              onClick={() => handleGetSuggestions(index)}
                              disabled={suggestionLoading[index]}
                              className="flex items-center gap-1 px-2 py-1 text-xs bg-purple-500 text-white rounded hover:bg-purple-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              title="Get AI suggestions for alternate names"
                            >
                              {suggestionLoading[index] ? (
                                <span className="animate-spin w-3 h-3 border-2 border-white border-t-transparent rounded-full" />
                              ) : (
                                <SparklesIcon className="w-4 h-4" />
                              )}
                              Suggest
                            </button>
                            {alternateNames[index] && (
                              <span className="text-green-600 text-xs">
                                ✔{" "}
                                {
                                  alternateNames[index]
                                    .split(",")
                                    .filter(Boolean).length
                                }{" "}
                                alternate name(s)
                              </span>
                            )}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>

                  <div className="flex justify-end mt-4">
                    <button
                      type="button"
                      className="bg-gray-200 text-gray-700 px-4 py-2 mr-2 rounded hover:bg-gray-400"
                      onClick={handleClosePopup}
                      ref={cancelButtonRef}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700"
                      onClick={handleMakeSubCity}
                      disabled={selectedOption === null}
                    >
                      Confirm
                    </button>
                  </div>
                </div>

                {loading && <Loading />}
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
}